// frontend/web/src/audio/bank.js
//
// AudioBank — alias-keyed SFX loader and player backed by the Web Audio API.
//
// Phase 2 responsibilities:
//   - Lazy, idempotent AudioContext unlock via init(gestureEvent).
//   - Load manifest.json and decode every referenced file into an AudioBuffer.
//   - Play cues by alias through the bus wired in buses.js.
//   - Dispatch batches of {type:'play'} commands emitted by EffectSchedulers.
//   - Hot-reload the manifest for the preview harness (Phase 5).
//
// Explicit NON-responsibilities in Phase 2:
//   - Attaching any gesture listener in the live game. init() is a method,
//     not a call site. Preview harness and Phase 6 integration own the
//     actual call sites.
//   - Ducking / compression / side-chaining. Flat gain for now.
//   - Replacing the existing <audio> element voice narration in
//     gameserver-connection.js. That swap is deferred.
//
// Usage:
//   import { audioBank } from './bank.js';
//   button.addEventListener('click', async (ev) => {
//     await audioBank.init(ev);
//     await audioBank.load();
//     audioBank.play('die.d20.roll');
//   });

import { createBuses } from './buses.js';

export class AudioBank {
  constructor() {
    // Per-alias round-robin position. Lives here rather than in the manifest
    // because it is PLAYBACK state, not authored data.
    this._variantCursor = new Map();
    this._ctx = null;
    this._buses = null;
    this._manifest = null;
    this._manifestUrl = null;
    this._buffers = new Map();   // alias -> AudioBuffer[] (one or more variants)
    this._initialized = false;
    this._loadPromise = null;    // de-dupes concurrent load() calls
    this._levels = null;         // bus calibration (levels.json)
    this._levelsUrl = null;
  }

  /**
   * Idempotent, gesture-agnostic AudioContext unlock.
   *
   * Safe to call from any gesture callback (click, keydown, touchend).
   * Safe to call more than once — subsequent calls are a no-op.
   * The gestureEvent parameter is accepted for Safari's stricter "must
   * be called in a gesture handler" rule; we don't read it, but taking
   * it in the signature keeps call sites honest.
   */
  async init(_gestureEvent) {
    if (this._initialized) return;

    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) {
      console.warn('[AudioBank] Web Audio API unavailable; audio disabled.');
      this._initialized = true; // mark so we don't retry
      return;
    }

    this._ctx = new Ctor();

    // Some browsers (Safari, older Chrome) start suspended even inside a
    // gesture callback until you explicitly resume().
    if (this._ctx.state === 'suspended') {
      try {
        await this._ctx.resume();
      } catch (err) {
        console.warn('[AudioBank] ctx.resume() failed:', err);
      }
    }

    // Buses are created with current levels (null on first init = defaults).
    // If loadLevels() / applyLevels() runs later, applyLevels() rewrites
    // GainNode.gain.value in place — no bus rebuild needed.
    this._buses = createBuses(this._ctx, this._levels);
    this._initialized = true;
  }

  /**
   * Load manifest.json and decode every referenced cue into memory.
   *
   * Must be called after init(). Concurrent calls de-dupe on the same
   * promise. Subsequent load() calls with the same URL are a no-op;
   * use reloadManifest(url) for explicit hot-reload.
   */
  async load(manifestUrl = '/audio/manifest.json') {
    if (!this._initialized) {
      console.warn('[AudioBank] load() called before init(); ignoring.');
      return;
    }
    if (this._manifest && this._manifestUrl === manifestUrl) return;
    if (this._loadPromise) return this._loadPromise;

    this._loadPromise = this._loadInternal(manifestUrl).finally(() => {
      this._loadPromise = null;
    });
    await this._loadPromise;

    // Lazy-load levels alongside the manifest so a single load() call
    // gives callers a calibrated bank. Failure here is non-fatal — the
    // bank still plays, just at default gains.
    if (!this._levels) {
      try {
        await this.loadLevels();
      } catch (err) {
        console.warn('[AudioBank] levels.json load failed (using defaults):', err);
      }
    }
  }

  async _loadInternal(manifestUrl) {
    const res = await fetch(manifestUrl);
    if (!res.ok) {
      throw new Error(
        `[AudioBank] failed to fetch manifest: ${manifestUrl} (${res.status})`
      );
    }
    const manifest = await res.json();
    if (!manifest || typeof manifest !== 'object' || !manifest.cues) {
      throw new Error('[AudioBank] manifest.json missing "cues" object');
    }

    // A cue declares ONE `src`, or a LIST of interchangeable variants
    // ([2026-08-24]). Variants exist because a REPEATED sample stops reading as
    // an event and starts reading as a machine: 3d6 fires `die.d20.settle`
    // once per die, so a single buffer plays the identical hit three times in
    // half a second. Five near-identical takes make the same three settles read
    // as a drum figure instead.
    //
    // ⚠️ A PARTIAL LOAD KEEPS WHAT LOADED. If three of five variants decode,
    // the cue plays those three rather than refusing -- a missing variant is a
    // thinner round-robin, not a silent cue. But a cue that loads NOTHING is
    // left out of `_buffers` entirely, so `play()` warns by alias instead of
    // failing silently on an empty list.
    const loads = Object.entries(manifest.cues).map(async ([alias, cue]) => {
      const sources = Array.isArray(cue.src) ? cue.src : [cue.src];
      const decodedAll = [];
      for (let i = 0; i < sources.length; i++) {
        try {
          // Pre-build script stages files under /audio/<basename>.ogg by
          // flattening the AudioFX/ tree. Resolve the staged URL from
          // the source path the manifest declares.
          const stagedUrl = this._stagedUrlFor(sources[i]);
          const response = await fetch(stagedUrl);
          if (!response.ok) {
            console.warn(
              `[AudioBank] cue '${alias}' variant ${i} fetch failed: ` +
              `${stagedUrl} (${response.status})`
            );
            continue;
          }
          const arrayBuf = await response.arrayBuffer();
          decodedAll.push(await this._ctx.decodeAudioData(arrayBuf));
        } catch (err) {
          console.warn(`[AudioBank] cue '${alias}' variant ${i} failed to load:`, err);
        }
      }
      if (decodedAll.length) {
        this._buffers.set(alias, decodedAll);
      } else {
        console.warn(`[AudioBank] cue '${alias}' loaded NO playable variant.`);
      }
    });

    await Promise.all(loads);
    this._manifest = manifest;
    this._manifestUrl = manifestUrl;

    const total = this._buffers.size;
    const expected = Object.keys(manifest.cues).length;
    console.info(
      `[AudioBank] manifest loaded: ${total}/${expected} cues decoded from ${manifestUrl}`
    );
  }

  /**
   * Compute the staged URL for a source path declared in manifest.json.
   *
   * The pre-build script flattens `AudioFX/<pack>/Audio/<file>.ogg` to
   * `public/audio/<file>.ogg`, which Vite serves at `/audio/<file>.ogg`.
   * If a bare filename collision ever happens between packs, the
   * build script will warn and we'll introduce a namespaced filename —
   * the alias layer here stays untouched.
   */
  _stagedUrlFor(srcPath) {
    const norm = String(srcPath).replace(/\\/g, '/');
    const basename = norm.split('/').pop();
    return `/audio/${basename}`;
  }

  /**
   * Is this alias loaded and playable RIGHT NOW?
   *
   * Exists so callers can OFFER a cue without asserting it. Two needs:
   *
   *   1. A cue may be deliberately SILENT. Clearing a cue's variants is how an
   *      author says "this hook exists and I do not want it to sound"; without
   *      `has()` the only way to discover that is to `play()` it and read a
   *      console warning, which turns a deliberate choice into a reported error.
   *   2. A caller may RESOLVE among candidates -- `die.d6.settle`, else the
   *      generic `die.settle` -- which requires asking before playing.
   *
   * Returns false (never throws) before init/load, so a caller written against
   * it degrades to silence rather than to an exception.
   */
  has(alias) {
    if (!this._manifest) return false;
    const variants = this._buffers.get(alias);
    return !!(this._manifest.cues[alias] && variants && variants.length);
  }

  /**
   * Play a cue by alias. Safe to call before init() or before load() —
   * both are warned and no-ops. Returns the AudioBufferSourceNode (or
   * undefined on failure) so callers can chain .stop() if needed.
   */
  play(alias, opts = {}) {
    if (!this._initialized) {
      console.warn(`[AudioBank] play('${alias}') before init(); ignoring.`);
      return undefined;
    }
    if (!this._manifest) {
      console.warn(`[AudioBank] play('${alias}') before load(); ignoring.`);
      return undefined;
    }
    const cue = this._manifest.cues[alias];
    const variants = this._buffers.get(alias);
    if (!cue || !variants || !variants.length) {
      console.warn(`[AudioBank] unknown alias: ${alias}`);
      return undefined;
    }

    // ROUND-ROBIN, NOT RANDOM. Random repeats -- with five variants it plays
    // the same take twice in a row about one roll in five, which is exactly the
    // machine-gun artefact variants exist to remove, and it would show up most
    // on the fastest sequence (three dice settling). A cursor per alias
    // guarantees the next hit is a different take.
    const cursor = this._variantCursor.get(alias) || 0;
    const buffer = variants[cursor % variants.length];
    this._variantCursor.set(alias, (cursor + 1) % variants.length);

    const src = this._ctx.createBufferSource();
    src.buffer = buffer;
    if (opts.playbackRate != null) {
      src.playbackRate.value = opts.playbackRate;
    }

    const gain = this._ctx.createGain();
    const optVol = opts.volume != null ? opts.volume : 1.0;
    const cueVol = cue.volume != null ? cue.volume : 1.0;
    gain.gain.value = optVol * cueVol;

    const busName = cue.bus && this._buses[cue.bus] ? cue.bus : 'sfx';
    src.connect(gain).connect(this._buses[busName]);

    const delaySec = (opts.delayMs || 0) / 1000;
    src.start(this._ctx.currentTime + delaySec);
    return src;
  }

  /**
   * Play a sound the SERVER handed over on a frame (effect-sounds): one moment
   * of a store-authored cue, `{moment, urls, volume, cue}`. The client maps no
   * name to a sound; it plays what it is given, on the sfx bus.
   *
   * Variants play round-robin per `(cue, moment)`, like `play()`. A buffer not
   * yet decoded is fetched and decoded first (a cast's outcome sounds are
   * `preload()`ed on its windup row so this is normally a cache hit). Absent or
   * empty is silence. A fetch or decode failure is LOUD and never cached.
   */
  async playHanded(sound, opts = {}) {
    if (!sound || !Array.isArray(sound.urls) || !sound.urls.length) return undefined;
    if (!this._initialized || !this._ctx || !this._buses) {
      console.warn(`[AudioBank] playHanded(${sound.cue}/${sound.moment}) before init(); ignoring.`);
      return undefined;
    }
    const key = `${sound.cue || '?'}:${sound.moment || '?'}`;
    const cursor = this._variantCursor.get(key) || 0;
    const url = sound.urls[cursor % sound.urls.length];
    this._variantCursor.set(key, (cursor + 1) % sound.urls.length);

    let buffer;
    try {
      buffer = await this._urlBuffer(url);
    } catch (err) {
      return undefined; // _urlBuffer already reported it
    }
    const src = this._ctx.createBufferSource();
    src.buffer = buffer;
    const gain = this._ctx.createGain();
    const optVol = opts.volume != null ? opts.volume : 1.0;
    const cueVol = sound.volume != null ? Number(sound.volume) : 1.0;
    gain.gain.value = optVol * cueVol;
    src.connect(gain).connect(this._buses.sfx);
    src.start(this._ctx.currentTime + (opts.delayMs || 0) / 1000);
    return src;
  }

  /** Fetch and decode `urls` ahead of need (a cast's hit / miss on its windup). */
  preload(urls) {
    if (!this._initialized || !this._ctx || !Array.isArray(urls)) return;
    for (const url of urls) this._urlBuffer(url).catch(() => {});
  }

  /** The decoded buffer for a handed URL; one fetch per URL, failures not cached. */
  _urlBuffer(url) {
    this._urlBuffers = this._urlBuffers || new Map();
    let pending = this._urlBuffers.get(url);
    if (!pending) {
      pending = (async () => {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return this._ctx.decodeAudioData(await res.arrayBuffer());
      })().catch((err) => {
        this._urlBuffers.delete(url);
        console.error(`[AudioBank] handed sound FAILED to load: ${url}`, err);
        throw err;
      });
      this._urlBuffers.set(url, pending);
    }
    return pending;
  }

  /**
   * Dispatch a batch of audio commands emitted by an EffectScheduler
   * tick. Each command currently has shape `{ type: 'play', alias, opts }`;
   * future types (stop, duck, loop-start) can be added here without
   * changing Effect contracts.
   */
  dispatch(commands) {
    if (!Array.isArray(commands) || commands.length === 0) return;
    for (const cmd of commands) {
      if (!cmd || typeof cmd !== 'object') continue;
      if (cmd.type === 'play') {
        this.play(cmd.alias, cmd.opts || {});
      } else {
        console.warn(`[AudioBank] unknown command type: ${cmd.type}`);
      }
    }
  }

  /**
   * Hot-reload the manifest — preview harness uses this when tuning
   * cue selections without a full page reload. Drops decoded buffers
   * and re-fetches + re-decodes from the given URL.
   */
  async reloadManifest(manifestUrl = this._manifestUrl || '/audio/manifest.json') {
    this._buffers.clear();
    this._manifest = null;
    this._manifestUrl = null;
    await this.load(manifestUrl);
  }

  // --- Levels (bus calibration) ---
  //
  // Levels live in `audio/levels.json` and are SEPARATE from the
  // manifest because tuning iterations should be hot-reloadable
  // independently of cue definitions. The shape is:
  //
  //   {
  //     "version": 1,
  //     "reference": "voice",
  //     "buses": {
  //       "voice":   { "gain": 1.00 },
  //       "sfx":     { "gain": 2.80 },
  //       "ambient": { "gain": 0.70 }
  //     },
  //     "master": 1.00
  //   }
  //
  // `applyLevels()` is the hot-reload path: it stores the new object
  // and updates each bus GainNode.gain via `setTargetAtTime` for a
  // short, click-free ramp. Phase 5's preview harness will call this
  // from slider input handlers; live game code can call it to honor a
  // user-facing volume setting later.

  /**
   * Fetch levels.json and apply it to the live bus graph. Idempotent —
   * a successful call sets `this._levels` and `this._levelsUrl`. May
   * be called before init() (the values are stashed and applied when
   * init() builds the buses), or after init() (mutates existing nodes).
   */
  async loadLevels(levelsUrl = '/audio/levels.json') {
    const res = await fetch(levelsUrl);
    if (!res.ok) {
      throw new Error(
        `[AudioBank] failed to fetch levels: ${levelsUrl} (${res.status})`
      );
    }
    const levels = await res.json();
    this._levelsUrl = levelsUrl;
    this.applyLevels(levels);
    console.info(
      `[AudioBank] levels loaded from ${levelsUrl}: ` +
      `voice=${levels?.buses?.voice?.gain ?? '?'}, ` +
      `sfx=${levels?.buses?.sfx?.gain ?? '?'}, ` +
      `ambient=${levels?.buses?.ambient?.gain ?? '?'}, ` +
      `master=${levels?.master ?? '?'}`
    );
  }

  /**
   * Hot-reload path. Stores the new levels object and rewrites each
   * bus GainNode.gain.value via a 20ms exponential ramp so live edits
   * (preview harness sliders, future user volume settings) don't
   * click. Safe to call before init() — values are stashed and
   * applied during the next createBuses() call.
   */
  applyLevels(levels) {
    if (!levels || typeof levels !== 'object' || !levels.buses) {
      console.warn('[AudioBank] applyLevels: invalid levels object; ignoring.');
      return;
    }
    this._levels = levels;

    if (!this._buses || !this._ctx) {
      // Pre-init: createBuses() will read this._levels at construction.
      return;
    }

    const master = typeof levels.master === 'number' ? levels.master : 1;
    const now = this._ctx.currentTime;
    const ramp = 0.02;

    const setBus = (busName, defaultGain) => {
      const bus = this._buses[busName];
      const cfg = levels.buses[busName];
      if (!bus) return;
      const target = master * (cfg && typeof cfg.gain === 'number' ? cfg.gain : defaultGain);
      // setTargetAtTime is the click-free knob for live tuning. The
      // time constant (0.02s) reaches ~63% of target in 20ms — fast
      // enough to feel instant but smooth enough to avoid pops.
      try {
        bus.gain.setTargetAtTime(target, now, ramp);
      } catch (_err) {
        bus.gain.value = target;
      }
    };

    setBus('voice', 1.0);
    setBus('sfx', 1.0);
    setBus('ambient', 0.7);

    // ⚠️ MASTER IS ITS OWN NODE NOW — set it HERE and NOWHERE ELSE.
    // `setBus` above deliberately no longer folds master into each bus gain
    // (see buses.js). Re-adding it there would apply master twice.
    if (this._buses.master) {
      try {
        this._buses.master.gain.setTargetAtTime(master, now, ramp);
      } catch (_err) {
        this._buses.master.gain.value = master;
      }
    }

    // Narrator trims are hot too — the whole point of the balance desk is
    // moving one narrator against the others while listening.
    this._applyNarratorTrims();
  }

  /**
   * Re-apply per-narrator trim to every element currently routed through the
   * voice bus. Called on applyLevels() and whenever an element is routed.
   */
  // ⚠️ A WeakMap IS NOT ITERABLE, so the element->entry map alone cannot drive
  // a retrim pass. The live list is WeakRefs, pruned on every pass: narration
  // creates a NEW Audio element per chunk and discards the last one, so a
  // strong list here would pin every clip of the session (the source node
  // holds its mediaElement) and leak for the length of a play session.
  _applyNarratorTrims() {
    if (!this._routedList || !this._ctx) return;
    const now = this._ctx.currentTime;
    const alive = [];
    for (const ref of this._routedList) {
      const entry = ref.deref ? ref.deref() : ref;
      if (!entry) continue;
      alive.push(ref);
      const target = this.narratorTrim(entry.narrator);
      try {
        entry.trim.gain.setTargetAtTime(target, now, 0.02);
      } catch (_err) {
        entry.trim.gain.value = target;
      }
    }
    this._routedList = alive;
  }

  /**
   * The trim for one narrator, from `levels.narrators`. Defaults to 1.0 —
   * an UNMEASURED narrator must pass through unchanged, never be guessed at.
   */
  narratorTrim(narrator) {
    const table = this._levels && this._levels.narrators;
    if (!table || !narrator) return 1.0;
    const entry = table[narrator];
    return entry && typeof entry.gain === 'number' ? entry.gain : 1.0;
  }

  /**
   * Route an HTMLMediaElement (narration audio) into a bus, through a
   * per-element trim gain keyed on which narrator is speaking.
   *
   * ⚠️ THIS IS WHY THE VOICE BUS DID NOTHING. `levels.json` has always
   * declared a `voice` gain and called it the reference the sfx bus is priced
   * against, but narration is a plain `new Audio(url)` that never entered the
   * Web Audio graph -- so the voice fader moved a node with no input. It read
   * correct only because the element's default 1.0 and the bus's 1.0 agreed by
   * coincidence. [2026-09-01-001-ENGINE]
   *
   * ⚠️ `createMediaElementSource` IS ONE-SHOT AND PERMANENTLY RE-ROUTES THE
   * ELEMENT. After the call the element's audio reaches the speakers ONLY
   * through the graph -- so routing into a SUSPENDED context makes narration
   * SILENT. That is a worse failure than an unbalanced mix, so this refuses to
   * route unless the context is genuinely running, and says so out loud. An
   * unrouted element still plays natively; it is merely unfadeable.
   *
   * Calling twice on the same element throws InvalidStateError, so routed
   * elements are cached and a repeat call just updates the trim.
   *
   * @returns {boolean} true if the element is routed through the graph.
   */
  routeMediaElement(el, busName = 'voice', narrator = null) {
    if (!el) return false;
    if (!this._routed) this._routed = new WeakMap();

    const existing = this._routed.get(el);
    if (existing) {
      existing.narrator = narrator || existing.narrator;
      this._applyNarratorTrims();
      return true;
    }

    if (!this._ctx || !this._buses) {
      console.warn(
        '[AudioBank] routeMediaElement: bank not initialized; leaving ' +
        'narration on the native element (plays, but is NOT on the voice bus).'
      );
      return false;
    }
    // ⚠️ CROSS-ORIGIN MEDIA CANNOT BE TAPPED, AND FAILS SILENT. THIS REFUSAL
    // IS THE WHOLE REASON THIS GUARD EXISTS.
    //
    // `createMediaElementSource` on a cross-origin element that did not arrive
    // with CORS permission produces a node that outputs SILENCE. It does not
    // throw. And because the call PERMANENTLY re-routes the element, the
    // element's own output stops reaching the speakers too -- so tapping a
    // tainted element does not degrade playback, it ENDS it.
    //
    // Production narration is exactly that shape: `resolveAudioUrl()` returns
    // GANDALF's ABSOLUTE signed GCS URL on prod, and those URLs answer with no
    // `access-control-allow-origin`. Setting crossOrigin="anonymous" does not
    // help -- with no ACAO the load simply fails instead.
    //
    // ⚠️ LOCAL DEV MASKS THIS COMPLETELY. In dev the same function returns a
    // RELATIVE `/gandalf/...` path that the vite proxy serves SAME-ORIGIN, so
    // routing works perfectly on a dev machine and silences every narration in
    // production. That is how this shipped: it was verified in the one
    // environment where the defect cannot occur. [2026-09-01-001-ENGINE]
    //
    // Refusing keeps narration AUDIBLE and merely unfadeable, which is the
    // correct trade every time: an unbalanced narrator is a mixing problem, a
    // silent one is a broken game.
    const mediaSrc = el.currentSrc || el.src || '';
    // Defensive about `window`: this module is imported by node:test suites,
    // and a bare reference would throw there and refuse EVERY element --
    // turning a targeted guard into a blanket disable.
    const pageHref = (typeof window !== 'undefined' && window.location)
      ? window.location.href : 'http://localhost/';
    const pageOrigin = (typeof window !== 'undefined' && window.location)
      ? window.location.origin : 'http://localhost';
    let sameOrigin = false;
    try {
      sameOrigin = new URL(mediaSrc, pageHref).origin === pageOrigin;
    } catch (_e) {
      sameOrigin = false;
    }
    if (!sameOrigin) {
      console.warn(
        `[AudioBank] routeMediaElement: REFUSING to tap a cross-origin media ` +
        `element (${mediaSrc.slice(0, 80)}…). Tapping it would output SILENCE ` +
        `with no error. Narration plays natively and is NOT on the voice bus. ` +
        `To put it on the bus, serve narration same-origin or have the bucket ` +
        `send access-control-allow-origin.`
      );
      return false;
    }

    if (this._ctx.state !== 'running') {
      // NOT an error, and NOT silent. The first narration clip is often what
      // unlocks audio in the first place, so this branch is expected once.
      console.warn(
        `[AudioBank] routeMediaElement: context is '${this._ctx.state}', not ` +
        `'running' — refusing to route. Narration plays natively and is NOT ` +
        `on the voice bus for this clip.`
      );
      return false;
    }

    const bus = this._buses[busName] || this._buses.voice;
    if (!bus) {
      console.warn(`[AudioBank] routeMediaElement: no '${busName}' bus.`);
      return false;
    }

    let source;
    try {
      source = this._ctx.createMediaElementSource(el);
    } catch (err) {
      // InvalidStateError (already routed elsewhere), or a cross-origin
      // element the platform refuses to tap. Either way the element still
      // plays; report it rather than pretending it is on the bus.
      console.warn('[AudioBank] routeMediaElement: createMediaElementSource failed:', err);
      return false;
    }

    const trim = this._ctx.createGain();
    trim.gain.value = this.narratorTrim(narrator);
    source.connect(trim).connect(bus);

    const entry = { source, trim, bus: busName, narrator };
    this._routed.set(el, entry);
    if (!this._routedList) this._routedList = [];
    this._routedList.push(
      typeof WeakRef === 'function' ? new WeakRef(entry) : entry
    );
    console.info(
      `[AudioBank] narration routed -> ${busName} bus ` +
      `(narrator=${narrator || 'unknown'}, trim=${trim.gain.value})`
    );
    return true;
  }

  /**
   * True if this element's audio is flowing through the Web Audio graph.
   * The pin for [2026-09-01-001-ENGINE] P1 asserts on this.
   */
  isRouted(el) {
    return !!(this._routed && el && this._routed.get(el));
  }

  /**
   * An AnalyserNode tapped off the MASTER node, for the levels display.
   * Created lazily so a page that never opens a meter pays nothing.
   * Returns null before init() — a meter cannot exist before the graph does.
   */
  meter() {
    if (!this._ctx || !this._buses || !this._buses.master) return null;
    if (!this._meter) {
      const a = this._ctx.createAnalyser();
      a.fftSize = 2048;
      a.smoothingTimeConstant = 0.3;
      // A tap, not a link in the chain: master already reaches destination.
      this._buses.master.connect(a);
      this._meter = a;
    }
    return this._meter;
  }

  /**
   * Instantaneous peak and RMS of the master sum, 0..1+ (values ABOVE 1.0 are
   * real and mean the mix is clipping — the meter must not clamp them away,
   * because showing the clip is the entire point). Returns null if no graph.
   */
  meterRead() {
    const a = this.meter();
    if (!a) return null;
    if (!this._meterBuf || this._meterBuf.length !== a.fftSize) {
      this._meterBuf = new Float32Array(a.fftSize);
    }
    a.getFloatTimeDomainData(this._meterBuf);
    let peak = 0, sum = 0;
    for (let i = 0; i < this._meterBuf.length; i++) {
      const v = this._meterBuf[i];
      const abs = v < 0 ? -v : v;
      if (abs > peak) peak = abs;
      sum += v * v;
    }
    // `reduction` is how many dB the limiter is pulling RIGHT NOW (<= 0).
    // Reported so the safety net is VISIBLE: a limiter that quietly absorbed
    // the overshoot would train everyone to push further into it.
    const lim = this._buses && this._buses.limiter;
    const reduction = lim && typeof lim.reduction === 'number' ? lim.reduction : 0;
    return {
      peak,
      rms: Math.sqrt(sum / this._meterBuf.length),
      reduction,
      limited: !!lim,
    };
  }

  /**
   * The gain a cue ACTUALLY reaches the destination with:
   * callVolume x cueVolume x busGain x masterGain.
   *
   * This exists because nothing ever displayed it, and that is precisely how
   * five cues came to pass their manifest volume a SECOND time at the call
   * site and play at its square. [2026-09-01-001-ENGINE]
   */
  effectiveGain(alias, callVolume = 1.0) {
    const cue = this._manifest && this._manifest.cues && this._manifest.cues[alias];
    if (!cue) return null;
    const cueVol = typeof cue.volume === 'number' ? cue.volume : 1.0;
    const busName = cue.bus || 'sfx';
    const lv = this._levels || {};
    const busCfg = lv.buses && lv.buses[busName];
    const busGain = busCfg && typeof busCfg.gain === 'number' ? busCfg.gain : 1.0;
    const master = typeof lv.master === 'number' ? lv.master : 1.0;
    return {
      alias,
      call: callVolume,
      cue: cueVol,
      bus: busName,
      busGain,
      master,
      effective: callVolume * cueVol * busGain * master,
    };
  }

  /**
   * Returns the most recently loaded levels object (or null). The
   * preview harness reads this to seed slider initial positions.
   */
  getLevels() {
    return this._levels;
  }

  // --- Introspection helpers (preview harness / diagnostics) ---

  get initialized() { return this._initialized; }
  get loaded() { return !!this._manifest; }
  get contextState() { return this._ctx ? this._ctx.state : 'uninitialized'; }
  listAliases() {
    return this._manifest ? Object.keys(this._manifest.cues) : [];
  }
}

// Singleton — effect schedulers and the preview harness share one bank.
export const audioBank = new AudioBank();
